import { useEffect, useRef, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import useAxios from "../../utils/useAxios";
import { fetchEquipmentPage } from "../../services/FrequencyShift/equipmentGroupService";

const SEARCH_DEBOUNCE_MS = 300;

const getEquipmentOptionLabel = (option) => option?.tagNumber || option?.equipmentName || "";

/**
 * Async, server-searched equipment picker for the "related equipment"/
 * parent-equipment fields that used to filter an already-fully-loaded plant
 * equipment list - that full list is no longer fetched once equipment
 * groups are paginated, so this searches the database a page at a time as
 * the user types instead. Mirrors AddExistingComponentsModal's debounced,
 * abort-guarded search Autocomplete.
 *
 * The caller still owns `value`/`onChange` as full equipment objects, same
 * as the client-filtered Autocomplete this replaces - only where the option
 * list comes from changes. A `value` selected before the modal opened (e.g.
 * hydrated via fetchEquipmentByIds) renders correctly even before its own
 * row happens to appear in a search result.
 *
 * @param {object} props
 * @param {string | number} props.plantId
 * @param {boolean} [props.multiple]
 * @param {object | object[] | null} [props.value]
 * @param {(value: object | object[] | null, reason: string) => void} props.onChange -
 *   `reason` is MUI Autocomplete's own change reason ("selectOption",
 *   "removeOption", "clear", ...), for callers that need it (e.g. to stop a
 *   chip removal from clearing a required field down to nothing)
 * @param {string} [props.label]
 * @param {number | string} [props.excludeId] - omit this equipment id from results (e.g. exclude self)
 * @param {boolean} [props.required]
 * @param {boolean} [props.error]
 * @param {string} [props.helperText]
 * @param {boolean} [props.disabled]
 * @param {() => void} [props.onBlur]
 */
export default function EquipmentAutocomplete({
  plantId,
  multiple = false,
  value,
  onChange,
  label = "Equipment",
  excludeId = null,
  required = false,
  error = false,
  helperText = "",
  disabled = false,
  onBlur,
}) {
  const api = useAxios();
  const requestIdRef = useRef(0);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !plantId) return undefined;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const controller = new AbortController();

    const searchTimer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const page = await fetchEquipmentPage(api, {
          plantId,
          search: search.trim() || undefined,
          pageSize: 20,
          signal: controller.signal,
        });
        if (requestIdRef.current === requestId) {
          setOptions(
            excludeId != null
              ? page.items.filter((item) => item.id !== excludeId)
              : page.items,
          );
        }
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        console.error("Error searching equipment:", err);
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(searchTimer);
      controller.abort();
    };
  }, [api, excludeId, open, plantId, search]);

  return (
    <Autocomplete
      multiple={multiple}
      disableCloseOnSelect={multiple}
      options={options}
      loading={loading}
      filterOptions={(opts) => opts} // server already filtered
      getOptionLabel={getEquipmentOptionLabel}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      value={value ?? (multiple ? [] : null)}
      onChange={(_, selected, reason) => onChange(selected, reason)}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onInputChange={(_, next) => setSearch(next)}
      onBlur={onBlur}
      disabled={disabled}
      noOptionsText={plantId ? "No matching equipment" : "Choose a plant first"}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          fullWidth
          required={required}
          error={error}
          helperText={helperText}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
